import { IdPClaims, Session } from '../machine/types'
import { AdapterInitResult, IdPAdapter } from './IdPAdapter'

/**
 * Programmable {@link IdPAdapter} for tests.
 *
 * Behaviour is set per-call via a script of responses. If a script runs out,
 * the adapter falls back to a default success. Errors classified as "auth"
 * (terminal) vs anything else (transient) drive the
 * `REFRESH_FAILED_TERMINAL` / `REFRESH_FAILED_TRANSIENT` branches without
 * coupling tests to a specific IdP error shape.
 */
export interface MockResponse<T> {
    /** Resolve with this value. */
    resolve?: T
    /** Reject with this error. */
    reject?: Error
    /** If `reject` is set, treat as an auth (terminal) error. Defaults to false. */
    authError?: boolean
    /** Optional artificial delay in ms. */
    delayMs?: number
}

export interface MockAdapterConfig {
    name?: string
    /** Initial session restored by `init()`, or `null` for no restore. */
    initial?: Session | null
    /** Whether the adapter reports itself supported by `init()`. Defaults to true. */
    supported?: boolean
    /** Scripted responses for sequential `login()` calls. */
    login?: Array<MockResponse<Session>>
    /** Scripted responses for sequential `refresh()` calls. */
    refresh?: Array<MockResponse<Session>>
    /** Default factory used when a scripted slot is exhausted. */
    defaultLoginResponse?: () => Session
    /** Default factory used when a scripted slot is exhausted. */
    defaultRefreshResponse?: () => Session
}

export interface MockIdPAdapter extends IdPAdapter {
    /** Manually invalidate any session the adapter currently considers valid. */
    forceLogout: () => void
    /** Replace scripted login responses on the fly. */
    setLoginScript: (script: Array<MockResponse<Session>>) => void
    /** Replace scripted refresh responses on the fly. */
    setRefreshScript: (script: Array<MockResponse<Session>>) => void
    /** Inspect how many times each method has been called. */
    readonly calls: { init: number; login: number; refresh: number; logout: number }
}

const defaultUser = { id: 1, name: 'Mock User', displayName: 'Mock User' }

const buildSession = (overrides: Partial<Session> = {}): Session => ({
    accessToken: overrides.accessToken ?? 'mock-access-token',
    refreshToken: overrides.refreshToken ?? 'mock-refresh-token',
    idToken: overrides.idToken,
    claims: overrides.claims ?? {
        user: defaultUser,
        roles: ['MockRole'],
        expiresAt: Date.now() + 60_000,
    },
})

const settle = <T>(response: MockResponse<T>): Promise<T> => {
    const work = response.delayMs
        ? new Promise<void>(resolve => setTimeout(resolve, response.delayMs))
        : Promise.resolve()
    return work.then(() => {
        if (response.reject) {
            return Promise.reject(response.reject)
        }
        return response.resolve as T
    })
}

const isAuthFlag = (response: MockResponse<unknown> | undefined): boolean =>
    !!response?.authError && !!response?.reject

export const createMockAdapter = (config: MockAdapterConfig = {}): MockIdPAdapter => {
    const name = config.name ?? 'mockAdapter'
    let loginScript = [...(config.login ?? [])]
    let refreshScript = [...(config.refresh ?? [])]
    const calls = { init: 0, login: 0, refresh: 0, logout: 0 }
    let currentSession: Session | null = config.initial ?? null
    /** Tracks the latest auth-flagged error so isAuthError can classify it. */
    const authErrors = new WeakSet<Error>()

    const consume = <T>(script: Array<MockResponse<T>>, fallback: () => T): Promise<T> => {
        const next = script.shift()
        if (!next) {
            return Promise.resolve(fallback())
        }
        if (isAuthFlag(next) && next.reject) {
            authErrors.add(next.reject)
        }
        return settle(next)
    }

    const init = async (): Promise<AdapterInitResult> => {
        calls.init += 1
        if (currentSession) {
            return { session: currentSession, supported: true }
        }
        return { session: null, supported: config.supported ?? true }
    }

    const login = async (): Promise<Session> => {
        calls.login += 1
        const session = await consume(
            loginScript,
            config.defaultLoginResponse ?? (() => buildSession()),
        )
        currentSession = session
        return session
    }

    const refresh = async (_refreshToken: string | undefined): Promise<Session> => {
        calls.refresh += 1
        const session = await consume(
            refreshScript,
            config.defaultRefreshResponse ??
                (() =>
                    buildSession({
                        accessToken: `refreshed-${calls.refresh}`,
                        refreshToken: `refresh-${calls.refresh}`,
                    })),
        )
        currentSession = session
        return session
    }

    const logout = async (_idToken: string | undefined): Promise<void> => {
        calls.logout += 1
        currentSession = null
    }

    const isAuthError = (err: unknown): boolean => err instanceof Error && authErrors.has(err)

    const parseClaims = (_accessToken: string): IdPClaims => {
        if (!currentSession) {
            throw new Error('No session — parseClaims called before login')
        }
        return currentSession.claims
    }

    return {
        name,
        init,
        login,
        refresh,
        logout,
        isAuthError,
        parseClaims,
        forceLogout: () => {
            currentSession = null
        },
        setLoginScript: script => {
            loginScript = [...script]
        },
        setRefreshScript: script => {
            refreshScript = [...script]
        },
        calls,
    }
}

/** Build a synthetic session — useful in tests when you don't need scripting. */
export const mockSession = buildSession
